import type { PatientGroupClassListItem } from '~/components/maud/patients/profile/tabs/appointments/groupClasses';
import { GET_GROUP_CLASS_PAGE_SIZE_V2 } from '~/lib/constants/groupClasses.constants';
import urlConstants from '~/lib/constants/url.constants';
import { CacheTag } from '~/store/services/CacheTag';
import { enrichGroupListWithEnumerations } from '~/lib/util/groups.utils';

import api from './api';
import { enumerationApi } from './enumerations';
import { ContentTypes, HttpHeaders, HttpRequestMethod } from '..';
import { ApiCommand } from '~/lib/Api';

const {
  patients: {
    autoRegisterPatient: autoRegisterPatientUrl,
    patientListV5,
    getDuplicateContacts,
    getPatientById,
    createPatientAccount,
    getPatientContactsByInquiryId,
    getGroupsCount,
    getGroupsCountV2,
    getCareTeams,
    getPatientContactById,
    getPatientAndSupportAvailableGroupsById,
    getPatientAndSupportAvailableGroupsByIdV2,
    deletePatientSupportLinkById,
    deletePatientContactById,
    linkPatientToExistingSupport,
    putBasicInfo,
    putTagsInfo,
    setActive,
    addPatientContactById,
    editPatientContactById,
    getSupportForEmail,
  },
  calendar: {
    patientAndSupportsRegisteredGroupClassesList,
    patientAndSupportsRegisteredGroupClassesListV2,
  },
  users: { supports, editSupportById, matchPatients },
} = urlConstants;

interface GroupClassRequest {
  groupClassExternalId: string;
  userId: string;
}

interface UpdateGroupSectionRegistrationRequest {
  groupSectionId: string;
  isRegistered: boolean;
  patientId: string;
  userExternalId: string;
}

interface ResentInvitationRequest {
  isProvider: boolean;
  isSupport: boolean;
  userId: string;
}

type SubmitBasicInfoRequest = {
  patientId: string;
  patientBody: Patient;
};

type SubmitTagInfoRequest = { patientId: string; tags: string[] };

enum EndpointKey {
  GET_DUPLICATE_CONTACTS = 'getDuplicateContacts',
  GET_GROUP_CLASS_COUNTS_OF_PATIENTS_AND_SUPPORTS = 'getPatientAndSupportRegisteredGroupClassCounts',
  GET_GROUP_CLASS_COUNTS_OF_PATIENTS_AND_SUPPORTS_V2 = 'getPatientAndSupportRegisteredGroupClassCountsV2',
  GET_PATIENT_AND_SUPPORT_REGISTERED_GROUPS = 'getPatientAndSupportRegisteredGroupClasses',
  GET_PATIENT_AND_SUPPORT_REGISTERED_GROUPS_V2 = 'getPatientAndSupportRegisteredGroupClassesV2',
}

interface PatientAndSupportRegisteredGroupClassesV2ResponseItem {
  externalId: string;
  firstName: string;
  lastName: string;
  userType?: string;
  groupSections?: {
    groupId?: string;
    groupSectionId?: string;
    title?: string;
    iconName?: string;
    groupType?: string;
    groupTypeCode?: string;
    frequencyCode?: SectionFrequencyCode;
    duration?: number;
    startDate: string;
    endDate?: string;
    length?: string;
    hosts: Array<{
      externalId: string;
      firstName?: string;
      displayFirstName?: string;
      lastName?: string;
      displayLastName?: string;
      type?: string;
      avatar?: {
        url?: string;
        thumbnailUrl?: string;
      };
    }>;
  }[];
}

type PatientAndSupportRegisteredGroupClassesV2Response =
  PatientAndSupportRegisteredGroupClassesV2ResponseItem[];

const {
  invitePatient: invitePatientUrl,
  inviteProvider: inviteProviderUrl,
  inviteSupport: inviteSupportUrl,
} = urlConstants.patients;

const getUrl = ({
  isProvider,
  isSupport,
  userId,
}: {
  isProvider: boolean;
  isSupport: boolean;
  userId: string;
}): string => {
  if (isProvider) return inviteProviderUrl(userId);
  if (isSupport) return inviteSupportUrl(userId);
  return invitePatientUrl(userId);
};

export const patientsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPatientsList: build.query<
      GetPatientsListResponse,
      GetPatientsListRequest
    >({
      keepUnusedDataFor: 10,
      query: ({ filterBy, pageNumber, pageSize }) => {
        const params = {
          ...filterBy,
          'page-number': pageNumber,
          'page-size': pageSize,
        };

        return {
          params,
          url: patientListV5,
        };
      },
    }),
    deletePatientSupportLink: build.mutation<
      void,
      DeletePatientSupportLinkRequest
    >({
      invalidatesTags: (_, __, arg) => [
        CacheTag.AppUsers,
        { id: arg.patientId, type: CacheTag.PatientContactById },
      ],
      query: ({ patientId, supportExternalId }) => ({
        method: 'DELETE',
        responseHandler: 'text',
        url: deletePatientSupportLinkById(supportExternalId, patientId),
      }),
    }),

    autoRegisterPatient: build.mutation<void, AutoRegisterPatientRequest>({
      query: ({ patientExternalId, admitDate }) => ({
        body: { admitDate },
        method: HttpRequestMethod.POST,
        url: autoRegisterPatientUrl(patientExternalId),
      }),
    }),

    addSupport: build.mutation<void, SaveSupportRequestBody>({
      invalidatesTags: (result, error, arg) => {
        // Only invalidate if not duplicate email error
        if (
          typeof error?.data === 'string' &&
          error?.data?.includes('Duplicate email found')
        ) {
          return [CacheTag.AppUsers];
        }
        return [
          CacheTag.AppUsers,
          { id: arg.patientExternalId, type: CacheTag.PatientContactById },
        ];
      },
      query: (body) => ({
        body,
        method: 'POST',
        responseHandler: 'text',
        url: supports,
      }),
    }),
    deletePatientContact: build.mutation<
      void,
      { contactExternalId: string; patientId: string; userId: string }
    >({
      invalidatesTags: (_, __, arg) => [
        { id: arg.patientId, type: CacheTag.PatientContactById },
      ],
      query: ({ contactExternalId, userId }) => ({
        method: HttpRequestMethod.DELETE,
        responseHandler: 'text',
        url: deletePatientContactById(contactExternalId, userId),
      }),
    }),
    editSupportById: build.mutation<void, EditSupportByIdRequest>({
      invalidatesTags: (result, error, arg) => {
        if (
          typeof error?.data === 'string' &&
          error?.data?.includes('Duplicate email found')
        ) {
          return [CacheTag.AppUsers];
        }
        return [
          CacheTag.AppUsers,
          { id: arg.body.patientExternalId, type: CacheTag.PatientContactById },
        ];
      },
      query: ({ supportExternalId, body }) => ({
        body,
        method: 'PUT',
        url: editSupportById(supportExternalId),
      }),
    }),
    [EndpointKey.GET_DUPLICATE_CONTACTS]: build.query<
      GetDuplicateContactsResponse,
      GetDuplicateContactsRequest
    >({
      providesTags: [CacheTag.DuplicateContacts],
      query: ({ inquiryId, patientId }) =>
        getDuplicateContacts(inquiryId, patientId),
    }),
    linkPatientToExistingSupport: build.mutation<
      void,
      LinkPatientToExistingSupportRequest
    >({
      invalidatesTags: (result, error, arg) => [
        CacheTag.AppUsers,
        { id: arg.body.patientExternalId, type: CacheTag.PatientContactById },
      ],

      query: ({ supportExternalId, body }) => ({
        body,
        method: 'POST',
        url: linkPatientToExistingSupport(supportExternalId),
      }),
    }),
    putBasicInfo: build.mutation<void, SubmitBasicInfoRequest>({
      invalidatesTags: (_, error) =>
        error ? [] : [CacheTag.AppUsers, CacheTag.GetPatientById],
      query: ({ patientId, patientBody }) => ({
        body: patientBody,
        method: 'PUT',
        url: putBasicInfo(patientId),
      }),
    }),
    putTagsInfo: build.mutation<void, SubmitTagInfoRequest>({
      query: ({ patientId, tags }) => ({
        body: {
          tags,
        },
        method: 'PUT',
        url: putTagsInfo(patientId),
      }),
    }),

    createPatientAppAccount: build.mutation<void, string>({
      invalidatesTags: [CacheTag.AppUsers, CacheTag.GetPatientById],
      query: (patientId) => ({
        body: {},
        method: 'POST',
        responseHandler: 'text',
        url: createPatientAccount(patientId),
      }),
    }),
    getPatient: build.query<
      Patient,
      { patientId: string; includeLinkedUsers?: boolean }
    >({
      providesTags: (_, __, { patientId }) => [
        { id: patientId, type: CacheTag.GetPatientById },
      ],
      query: ({ patientId, includeLinkedUsers }) =>
        getPatientById(patientId, includeLinkedUsers),
    }),
    [EndpointKey.GET_GROUP_CLASS_COUNTS_OF_PATIENTS_AND_SUPPORTS]: build.query<
      GroupsCount,
      string
    >({
      providesTags: [CacheTag.PatientAndSupportGroups],
      query: (patientExternalId) => getGroupsCount(patientExternalId),
    }),
    [EndpointKey.GET_GROUP_CLASS_COUNTS_OF_PATIENTS_AND_SUPPORTS_V2]:
      build.query<number, string>({
        providesTags: [CacheTag.PatientAndSupportGroups],
        query: (patientExternalId) => getGroupsCountV2(patientExternalId),
      }),
    getPatientCareTeam: build.query<PatientCareTeams, string>({
      query: (patientId) => getCareTeams(patientId),
    }),
    getPatientContacts: build.query<PatientContact[], string>({
      providesTags: (result, error, patientId) => [
        { id: patientId, type: CacheTag.PatientContactById },
      ],
      query: (patientId) => getPatientContactById(patientId),
    }),
    getPatientContactsByInquiryId: build.query<
      SuccessResponse<PatientContact[]>,
      string
    >({
      providesTags: (result, error, inquiryId) => [
        { id: inquiryId, type: CacheTag.PatientContactById },
        { id: inquiryId, type: CacheTag.Inquiry },
      ],
      query: (inquiryId) => getPatientContactsByInquiryId(inquiryId),
    }),
    getPatientOrSupportAvailableGroupsList: build.query<
      { data: { data: GroupClassBaseV2[]; count: number; pageNumber: number } },
      {
        patientId: string;
        userType: string;
        pageNumber: number;
        title?: string;
      }
    >({
      providesTags: [CacheTag.PatientAndSupportGroups],
      query: ({ patientId, userType, pageNumber, title }) => ({
        headers: {
          'Content-Type': 'application/json',
        },
        params: {
          'page-number': pageNumber,
          'page-size': GET_GROUP_CLASS_PAGE_SIZE_V2,
          title,
          'user-type': userType,
        },
        url: getPatientAndSupportAvailableGroupsById(patientId),
      }),
    }),

    getPatientOrSupportAvailableGroupsListV2: build.query<
      GroupListResponse,
      {
        patientId: string;
        pageNumber: number;
        title?: string;
        supportId?: string;
      }
    >({
      providesTags: [CacheTag.PatientAndSupportGroups],
      queryFn: async (
        { patientId, pageNumber, title, supportId },
        { dispatch },
        _extraOptions,
        baseQuery,
      ) => {
        const result = await baseQuery({
          headers: {
            'Content-Type': 'application/json',
          },
          params: {
            'page-number': pageNumber,
            'page-size': GET_GROUP_CLASS_PAGE_SIZE_V2,
            ...(title ? { title } : {}),
            ...(supportId ? { 'support-external-id': supportId } : {}),
          },
          url: getPatientAndSupportAvailableGroupsByIdV2(patientId),
        });

        if (result.error) return { error: result.error };

        const response = result?.data as GroupListResponse;

        if (!response?.data?.length)
          return {
            data: {
              data: [],
              count: 0,
              pageNumber: 1,
              pageSize: GET_GROUP_CLASS_PAGE_SIZE_V2,
            } as GroupListResponse,
          };

        // Fetch enumerations for enrichment
        const [programsResult, modalitiesResult, groupTypesResult] =
          await Promise.all([
            dispatch(
              enumerationApi.endpoints.getTreatmentPrograms.initiate(
                undefined,
                { forceRefetch: false, subscribe: false },
              ),
            ),
            dispatch(
              enumerationApi.endpoints.getTreatmentModalitiesEnum.initiate(
                undefined,
                { forceRefetch: false, subscribe: false },
              ),
            ),
            dispatch(
              enumerationApi.endpoints.getGroupTypesEnum.initiate(undefined, {
                forceRefetch: false,
                subscribe: false,
              }),
            ),
          ]);

        const programs = programsResult.data ?? [];
        const modalities = modalitiesResult.data ?? [];
        const groupTypes = groupTypesResult.data ?? [];

        const enrichedData = enrichGroupListWithEnumerations(
          response.data,
          programs,
          modalities,
          groupTypes,
        );

        return {
          data: {
            ...response,
            data: enrichedData,
          } as GroupListResponse,
        };
      },
    }),

    getPatientMatches: build.query<GetPatientMatches, GetPatientMatchesRequest>(
      {
        query: ({
          contactEmail,
          contactPhoneNumber,
          patientDateOfBirth,
          patientEmail,
          patientFirstName,
          patientLastName,
          patientPhoneNumber,
          relationWithPatient,
          supportFirstName,
          supportLastName,
        }) => {
          const body: GetPatientMatchesRequest = {
            contactEmail,
            contactPhoneNumber,
            patientEmail,
            patientFirstName,
            patientLastName,
            patientPhoneNumber,
            relationWithPatient,
            supportFirstName,
            supportLastName,
          };

          if (patientDateOfBirth) {
            body.patientDateOfBirth = patientDateOfBirth;
          }

          return {
            body,
            method: 'POST',
            url: matchPatients,
          };
        },
      },
    ),
    setPatientActive: build.mutation<void, SetPatientActiveRequest>({
      query: ({ patientId, isActive }) => ({
        body: { isActive },
        method: HttpRequestMethod.PUT,
        url: setActive(patientId),
      }),
    }),
    [EndpointKey.GET_PATIENT_AND_SUPPORT_REGISTERED_GROUPS]: build.query<
      { data: PatientGroupClassListItem[] },
      {
        patientId: string;
      }
    >({
      providesTags: [CacheTag.PatientAndSupportGroups],
      query: ({ patientId }) => ({
        url: patientAndSupportsRegisteredGroupClassesList(patientId),
      }),
    }),
    [EndpointKey.GET_PATIENT_AND_SUPPORT_REGISTERED_GROUPS_V2]: build.query<
      PatientGroupClassListItem[],
      {
        patientId: string;
      }
    >({
      providesTags: [CacheTag.PatientAndSupportGroups],
      queryFn: async ({ patientId }, { dispatch }, _extra, baseQuery) => {
        const mainResult = await baseQuery({
          url: patientAndSupportsRegisteredGroupClassesListV2(patientId),
        });

        const groupTypesResult = await dispatch(
          enumerationApi.endpoints.getGroupTypesEnum.initiate(undefined, {
            forceRefetch: false,
            subscribe: false,
          }),
        );

        if (mainResult.error) {
          return { error: mainResult.error };
        }

        const groupTypes = groupTypesResult.data ?? [];
        const response =
          (mainResult.data as PatientAndSupportRegisteredGroupClassesV2Response) ??
          [];

        const getGroupType = (groupTypeCode: string): string => {
          return (
            groupTypes.find((gt) => gt.code === groupTypeCode)?.type ??
            groupTypeCode
          );
        };

        return {
          data: response.map((item) => {
            const rawGroups = (item.groupSections ?? []) as any[];

            return {
              externalId: item.externalId,
              firstName: item.firstName,
              lastName: item.lastName,
              userType: item.userType,
              groups: rawGroups.map((group) => ({
                title: group.title,
                iconName: group.iconName,
                frequency: group.frequencyCode,
                groupClassExternalId: group.groupId,
                groupSectionId: group.groupSectionId,
                groupType: getGroupType(group.groupTypeCode),
                groupTypeCode: group.groupTypeCode,
                duration: group.duration,
                startDate: group.startDate,
                nextStartDate: group.startDate,
                endDate: group.endDate,
                length: group.length,
                isUserRegistered: true,
                isPromoted: false,
                groupActiveSectionCount: group.groupActiveSectionCount ?? 0,
                hosts: group.hosts,
              })),
            };
          }),
        };
      },
    }),

    registerPatientOrSupportForGroupClass: build.mutation<
      void,
      GroupClassRequest
    >({
      invalidatesTags: [CacheTag.PatientAndSupportGroups],

      query: ({ groupClassExternalId, userId }) => ({
        headers: {
          'Content-Type': 'application/json',
        },
        method: HttpRequestMethod.POST,
        url: urlConstants.groupClasses.registerPatientOrSupportForGroupClass(
          groupClassExternalId,
          userId,
        ),
      }),
    }),

    unregisterPatientOrSupport: build.mutation<
      void,
      { groupClassExternalId: string; userId: string }
    >({
      invalidatesTags: [CacheTag.PatientAndSupportGroups],

      query: ({ groupClassExternalId, userId }) => ({
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.DELETE,
        url: urlConstants.groupClasses.unRegisterPatientOrSupportForGroup(
          groupClassExternalId,
          userId,
        ),
      }),
    }),
    registerOrUnregisterPatientOrSupportFromGroupSection: build.mutation<
      void,
      UpdateGroupSectionRegistrationRequest
    >({
      invalidatesTags: [CacheTag.PatientAndSupportGroups],

      query: ({ groupSectionId, patientId, isRegistered, userExternalId }) => ({
        body: {
          isRegistered,
          userExternalId,
        },
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.PATCH,
        url: urlConstants.groupClasses.updateGroupSectionRegistrationForPatient(
          patientId,
          groupSectionId,
        ),
      }),
    }),
    addPatientContact: build.mutation<
      void,
      { body: PatientContact; patientId: string }
    >({
      query: ({ body, patientId }) => ({
        body,
        method: 'POST',
        responseHandler: 'text',
        url: addPatientContactById(patientId),
      }),
      invalidatesTags: (result, error, arg) => {
        if (
          typeof error?.data === 'string' &&
          error?.data?.includes('Duplicate email found')
        ) {
          return [];
        }
        return [{ id: arg.patientId, type: CacheTag.PatientContactById }];
      },
    }),

    inviteSupport: build.mutation<void, { supportId: string }>({
      query: ({ supportId }) => ({
        headers: {
          'content-type': 'application/json',
        },
        method: 'POST',
        responseHandler: 'text',
        url: urlConstants.patients.inviteSupport(supportId),
      }),
    }),
    invitePatient: build.mutation<void, string>({
      query: (patientId) => ({
        body: {},
        method: HttpRequestMethod.POST,
        responseHandler: 'text',
        url: urlConstants.patients.invitePatient(patientId),
      }),
    }),
    editPatientContact: build.mutation<
      void,
      { body: PatientContact; contactId: string }
    >({
      query: ({ body, contactId }) => ({
        body,
        method: 'PUT',
        responseHandler: 'text',
        url: editPatientContactById(contactId),
      }),
      invalidatesTags: (result, error, arg) => {
        if (
          typeof error?.data === 'string' &&
          error?.data?.includes('Duplicate email found')
        ) {
          return [];
        }
        return [
          { id: arg.body.patientExternalId, type: CacheTag.PatientContactById },
        ];
      },
    }),
    getSupportForEmail: build.query<PatientContact, string>({
      query: (email: string) => ({
        url: getSupportForEmail(encodeURIComponent(email)),
        method: 'GET',
      }),
    }),

    resendInvitation: build.mutation<void, ResentInvitationRequest>({
      query: (request) => ({
        body: {},
        method: ApiCommand.POST,
        responseHandler: 'text',
        url: getUrl({ ...request }),
      }),
    }),
  }),
});

export const {
  useAddSupportMutation,
  useCreatePatientAppAccountMutation,
  useDeletePatientContactMutation,
  useDeletePatientSupportLinkMutation,
  useEditSupportByIdMutation,
  useLazyGetPatientAndSupportRegisteredGroupClassCountsQuery,
  useLazyGetPatientAndSupportRegisteredGroupClassCountsV2Query,
  useLazyGetDuplicateContactsQuery,
  useGetPatientCareTeamQuery,
  useGetPatientContactsQuery,
  useLazyGetPatientOrSupportAvailableGroupsListQuery,
  useLazyGetPatientOrSupportAvailableGroupsListV2Query,
  useGetPatientsListQuery,
  useLinkPatientToExistingSupportMutation,
  usePutBasicInfoMutation,
  usePutTagsInfoMutation,
  useLazyGetPatientMatchesQuery,
  useGetPatientQuery,
  useGetPatientContactsByInquiryIdQuery,
  useSetPatientActiveMutation,
  useGetPatientAndSupportRegisteredGroupClassesQuery,
  useGetPatientAndSupportRegisteredGroupClassesV2Query,
  useUnregisterPatientOrSupportMutation,
  useRegisterOrUnregisterPatientOrSupportFromGroupSectionMutation,
  useRegisterPatientOrSupportForGroupClassMutation,
  useAddPatientContactMutation,
  useInviteSupportMutation,
  useEditPatientContactMutation,
  useGetSupportForEmailQuery,
  useInvitePatientMutation,
  useResendInvitationMutation,
  useAutoRegisterPatientMutation,
} = patientsApi;
